import { helpers } from '../..';
import ContextSingleton from '../../__singletons__/context-singleton';
import { AnalysisConfig, AnalysisRef, ResourceAnalysis } from '../../types/analysis';

export async function loadAndCreateAnalyses<T extends string, TResource = any>(
  config: AnalysisConfig<T, TResource>,
  resourceId: number,
  resourceType: ResourceAnalysis['item_type'],
  isSharedResource: boolean,
  resource: TResource
): Promise<AnalysisRef<T>[]> {
  try {
    const api = ContextSingleton.getInstance().api;
    // Search for existing analyses
    const resp = await api.resource_analyses.search({
      ignore_user_scope: isSharedResource,
      query: ['$and', ['item_id_ref', '=', resourceId], ['item_type', '=', resourceType]],
      include: 'id,type,name',
    });

    const existingAnalyses = ((resp.data || []) as ResourceAnalysis[]).map((a) => ({
      id: a.id,
      type: a.type as T,
      name: a.name,
    }));

    const existingTypes = existingAnalyses.map((a) => a.type);

    // Find missing analyses
    const toCreate = config.availableAnalyses.filter((type) => !existingTypes.includes(type)) as T[];

    // Create missing analyses using config defaults
    if (toCreate.length > 0) {
      const analysesToCreate = toCreate
        .map((type) => {
          const defaultConfig = config.default.find((d) => d.type === type);
          if (defaultConfig) {
            return {
              report_definition: defaultConfig.initialDefinition,
              item_id_ref: resourceId,
              item_type: resourceType,
              type,
              name: defaultConfig.name || type,
            };
          }
          return null;
        })
        .filter((analysis) => analysis !== null);

      if (analysesToCreate.length > 0) {
        const response = await api.combined.run({
          resource_analyses: {
            create: analysesToCreate,
          },
        });

        const createdAnalyses = ((response.resource_analyses?.create || []) as Array<{ data: ResourceAnalysis }>).map(
          (item) => ({
            id: item.data.id,
            type: item.data.type as T,
            name: item.data.name,
          })
        );
        const allAnalyses = [...existingAnalyses, ...createdAnalyses];

        // Apply filter if available and resource is provided
        if (config.filterAnalyses && resource) {
          const filteredTypes = config.filterAnalyses(
            resource,
            allAnalyses.map((a) => a.type)
          );
          return allAnalyses.filter((a) => filteredTypes.includes(a.type));
        }
        return allAnalyses;
      }
    }

    let response = existingAnalyses as AnalysisRef<T>[];
    // Apply filter if available and resource is provided
    if (config.filterAnalyses && resource) {
      const filteredTypes = config.filterAnalyses(resource, existingTypes);
      response = existingAnalyses.filter((a) => filteredTypes.includes(a.type));
    }

    return config.default
      .map((defaultAnalysis) => response.find((a) => a.type === defaultAnalysis.type))
      .filter((a) => !!a);
  } catch (error) {
    throw new Error(`Error loading/creating analyses - ${helpers.parseApiError(error as any)}`);
  }
}

export function validateAnalysisConfig<T extends string>(
  config: AnalysisConfig<T>
): asserts config is AnalysisConfig<T> {
  const defaultTypes = config.default.map((item) => item.type);
  const defaultSet = new Set(defaultTypes);

  const missing = config.availableAnalyses.filter((type) => !defaultSet.has(type));

  if (missing.length > 0) {
    throw new Error(`AnalysisConfig validation failed: Missing analysis types in default array: ${missing.join(', ')}`);
  }
}
